import { pdfjs } from 'react-pdf';

// Configure the PDF.js worker so page-count validation works without
// requiring the PdfViewer component to have been mounted first.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const processFileName = (fileName: string): string => {
  const parts = fileName.split(".");
  const extension = parts.pop();
  const nameWithoutExtension = parts.join(".");
  const processedName = nameWithoutExtension
    .replaceAll(/\s+/g, "_")
    .replaceAll('.', "_")
    .replaceAll('\'', "_")
    .replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  return processedName + "." + extension;
};

// Upload limit constants
export const MAX_FILE_SIZE_MB = 50;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_PAGE_COUNT = 250;

export const UPLOAD_LIMIT_ERROR_MESSAGE =
  "Upload failed: Your files must be 250 pages or fewer and 50 MB or smaller. Please reduce the file size/page count and try again.";

export const ALLOWED_EXTENSIONS = ['pdf'];

export interface FileValidationError {
  fileName: string;
  exceedsSize: boolean;
  exceedsPages: boolean;
  invalidType: boolean;
}

/**
 * Validate a single file against upload limits.
 * Checks file extension (PDF only), file size (50MB), and page count (250 pages).
 * Returns null if the file passes validation, or an error object describing the violation.
 */
export async function validateFile(file: File): Promise<FileValidationError | null> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const invalidType = !extension || !ALLOWED_EXTENSIONS.includes(extension);

  // If the file type is not allowed, reject immediately without further checks
  if (invalidType) {
    return {
      fileName: file.name,
      exceedsSize: false,
      exceedsPages: false,
      invalidType: true,
    };
  }

  const exceedsSize = file.size > MAX_FILE_SIZE_BYTES;
  let exceedsPages = false;

  // Check page count for PDF files
  if (extension === 'pdf') {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      if (pdf.numPages > MAX_PAGE_COUNT) {
        exceedsPages = true;
      }
      pdf.destroy();
    } catch {
      // If we can't read the PDF, skip page count validation
      // (the file may still fail at upload time for other reasons)
    }
  }

  if (exceedsSize || exceedsPages) {
    return {
      fileName: file.name,
      exceedsSize,
      exceedsPages,
      invalidType: false,
    };
  }

  return null;
}

/**
 * Validate multiple files against upload limits.
 * Returns an array of validation errors (empty if all files pass).
 */
export async function validateFiles(files: File[]): Promise<FileValidationError[]> {
  const results = await Promise.all(files.map(validateFile));
  return results.filter((r): r is FileValidationError => r !== null);
}
